import { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import styles from './DefinitionSelector.module.css';

/**
 * Single-select stain selector.
 * Modal with search and compact list of stain definitions; select one and close.
 */
const UnifiedStainSelector = ({
    stains = [],
    selectedStain = null,
    onSelectStain,
    onCreateNewStain,
    disabled = false,
    compact = false,
    emptyLabel = 'Choose stain…',
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    const filteredStains = useMemo(() => {
        if (!stains || !Array.isArray(stains)) return [];
        if (!searchTerm.trim()) return stains;
        const term = searchTerm.toLowerCase();
        return stains.filter(stain => {
            const name = (stain.name || '').toLowerCase();
            const vendor = (stain.details?.vendor || '').toLowerCase();
            return name.includes(term) || vendor.includes(term);
        });
    }, [stains, searchTerm]);

    const handleSelectStain = (stain) => {
        if (onSelectStain) {
            // Preserve schemaStainKey and schemaId (required by backend contract)
            // Backend naming function (naming.py) is single source of truth - it requires these fields
            // If fields are missing, backend will raise ValidationException (correct behavior)
            const selected = {
                _id: stain._id,
                name: stain.name,
                schemaStainKey: stain.schemaStainKey,  // Preserve as-is (backend will validate)
                schemaId: stain.schemaId,              // Preserve as-is (backend will validate)
                details: stain.details ? { ...stain.details } : {},
            };
            onSelectStain(selected);
        }
        setIsOpen(false);
        setSearchTerm('');
    };

    const handleCreateNew = () => {
        setIsOpen(false);
        setSearchTerm('');
        if (onCreateNewStain) {
            onCreateNewStain();
        }
    };

    const getButtonText = () => {
        if (selectedStain?.name) {
            return selectedStain.name;
        }
        return emptyLabel;
    };

    if (compact) {
        return (
            <div className={styles.compactContainer}>
                <button
                    type="button"
                    className={styles.compactButton}
                    onClick={(e) => { e.stopPropagation(); setIsOpen(true); }}
                    disabled={disabled}
                    title={selectedStain ? 'Change stain' : emptyLabel}
                >
                    <span className={styles.compactName}>
                        {selectedStain?.name || emptyLabel}
                    </span>
                </button>

                {isOpen && typeof document !== 'undefined' && createPortal(
                    <>
                        <div className={styles.overlay} onClick={() => setIsOpen(false)} />
                        <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                            <div className={styles.modalHeader}>
                                <h3>Select Stain</h3>
                                <button className={styles.closeButton} onClick={() => setIsOpen(false)}>×</button>
                            </div>
                            <div style={{ padding: '0 1.5em', fontSize: '13px', color: '#6a737d', marginTop: '0.5em' }}>
                                Choose a stain definition for this item.
                            </div>
                            <div className={styles.modalContent}>
                                <div className={styles.searchBox}>
                                    <input
                                        type="text"
                                        placeholder="Search stains..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        autoFocus
                                    />
                                </div>
                                <div className={`${styles.definitionsList} ${styles.definitionsListCompact}`}>
                                    {filteredStains.map(stain => {
                                        const isSelected = selectedStain && String(selectedStain._id) === String(stain._id);
                                        const detail = stain.details?.vendor;
                                        return (
                                            <div
                                                key={stain._id}
                                                className={`${styles.definitionCard} ${styles.definitionCardCompact} ${isSelected ? styles.selected : ''}`}
                                                onClick={(e) => { e.stopPropagation(); handleSelectStain(stain); }}
                                            >
                                                <div className={styles.cardHeader}>
                                                    <strong>{stain.name || '—'}</strong>
                                                    {isSelected && (
                                                        <span style={{ marginLeft: 'auto', color: '#28a745', fontWeight: '600' }}>✓</span>
                                                    )}
                                                </div>
                                                {detail && (
                                                    <div className={styles.cardDetails}>
                                                        <span>{detail}</span>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                    {filteredStains.length === 0 && (
                                        <div className={styles.emptyState}>
                                            {searchTerm ? 'No stains match your search' : 'No stains available'}
                                        </div>
                                    )}
                                </div>
                                {onCreateNewStain && (
                                    <div className={styles.modalFooter}>
                                        <button className={styles.createButton} onClick={handleCreateNew}>
                                            + Create New Stain
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </>,
                    document.body
                )}
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <button
                onClick={() => setIsOpen(true)}
                disabled={disabled}
                className={styles.select}
            >
                {getButtonText()}
            </button>
            {isOpen && typeof document !== 'undefined' && createPortal(
                <>
                    <div className={styles.overlay} onClick={() => setIsOpen(false)} />
                    <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h3>Select Stain</h3>
                            <button className={styles.closeButton} onClick={() => setIsOpen(false)}>×</button>
                        </div>
                        <div className={styles.modalContent}>
                            <div className={styles.searchBox}>
                                <input
                                    type="text"
                                    placeholder="Search stains..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    autoFocus
                                />
                            </div>
                            <div className={styles.definitionsList}>
                                {filteredStains.map(stain => {
                                    const isSelected = selectedStain && String(selectedStain._id) === String(stain._id);
                                    return (
                                        <div
                                            key={stain._id}
                                            className={`${styles.definitionCard} ${isSelected ? styles.selected : ''}`}
                                            onClick={(e) => { e.stopPropagation(); handleSelectStain(stain); }}
                                        >
                                            <div className={styles.cardHeader}>
                                                <strong>{stain.name || '—'}</strong>
                                                {isSelected && <span style={{ marginLeft: 'auto', color: '#28a745' }}>✓</span>}
                                            </div>
                                            {stain.details?.vendor && (
                                                <div className={styles.cardDetails}>
                                                    <span>{stain.details.vendor}</span>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                                {filteredStains.length === 0 && (
                                    <div className={styles.emptyState}>
                                        {searchTerm ? 'No stains match your search' : 'No stains available'}
                                    </div>
                                )}
                            </div>
                            {onCreateNewStain && (
                                <div className={styles.modalFooter}>
                                    <button className={styles.createButton} onClick={handleCreateNew}>
                                        + Create New Stain
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </>,
                document.body
            )}
        </div>
    );
};

export default UnifiedStainSelector;
